import React, { useState, useEffect, useRef } from 'react';
import { useDomainConfig } from '@/hooks/useDomainConfig';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useMicrosoftClarity } from '@/hooks/useMicrosoftClarity';

interface DownloadFormProps {
  onSubmit: (url: string) => void;
  isLoading: boolean;
  className?: string;
  prefilledUrl?: string;
  platformHint?: string;
}

const DownloadForm: React.FC<DownloadFormProps> = ({
  onSubmit,
  isLoading,
  className = '',
  prefilledUrl = '',
  platformHint
}) => {
  const config = useDomainConfig();
  const { trackVideoSearch } = useGoogleAnalytics();
  const { trackVideoSearch: trackClarityVideoSearch } = useMicrosoftClarity();
  const [url, setUrl] = useState(prefilledUrl);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setUrl(prefilledUrl);
    
    // Auto-focus and position cursor at the end when there's a prefilled URL
    if (prefilledUrl && inputRef.current) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Position cursor at the end of the text
          inputRef.current.setSelectionRange(prefilledUrl.length, prefilledUrl.length);
          console.log('✅ Auto-focused input field with prefilled URL');
        }
      }, 100);
    }
  }, [prefilledUrl]);

  const validateUrl = (inputUrl: string): boolean => {
    const trimmedUrl = inputUrl.trim();
    
    if (!trimmedUrl) {
      setError('Please enter a video URL');
      return false;
    }

    // For Xiaohongshu and Bilibili, allow text messages that contain URLs
    if (platformHint === 'Xiaohongshu' || platformHint === 'Bilibili') {
      // Check if input contains a URL or starts with http/https
      const hasUrl = trimmedUrl.match(/https?:\/\/[^\s]+/) || trimmedUrl.match(/^https?:\/\/.+/);
      if (!hasUrl) {
        if (platformHint === 'Xiaohongshu') {
          setError('Please enter a valid Xiaohongshu URL or RedNote app message containing a URL');
        } else {
          setError('Please enter a valid Bilibili URL or Bilibili app message containing a URL');
        }
        return false;
      }
    } else {
      // For other platforms, require direct URL format
      if (!trimmedUrl.match(/^https?:\/\/.+/)) {
        setError('Please enter a valid URL (must include http:// or https://)');
        return false;
      }
    }

    setError('');
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateUrl(url)) {
      const trimmedUrl = url.trim();
      // Track the search event for analytics
      trackVideoSearch(trimmedUrl);
      trackClarityVideoSearch(trimmedUrl);
      onSubmit(trimmedUrl);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
    if (error) setError(''); // Clear error when user starts typing
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit(e as any);
    }
  };

  const clearInput = () => {
    setUrl('');
    setError('');
  };

  return (
    <div className={`w-full max-w-6xl mx-auto ${className}`}>
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Main Input Section */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <div className="relative w-full sm:w-[600px] lg:w-[700px] xl:w-[800px]">
              <input
                ref={inputRef}
                type={platformHint === 'Xiaohongshu' || platformHint === 'Bilibili' ? 'text' : 'url'}
                value={url}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder={
                  platformHint === 'Xiaohongshu'
                    ? "Paste Xiaohongshu URL or RedNote app message here... (Press Enter to download)"
                    : platformHint === 'Bilibili'
                    ? "Paste Bilibili URL or Bilibili app message here... (Press Enter to download)"
                    : platformHint
                    ? `Paste ${platformHint} URL here... (Press Enter to download)`
                    : "Paste video or podcast URL here... (Press Enter to download)"
                }
                className={`w-full px-6 py-4 text-lg border-2 rounded-lg transition-all duration-200 pr-12 ${
                  error
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200'
                } focus:outline-none`}
                disabled={isLoading}
              />

              {/* Clear button or link icon */}
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                {url ? (
                  <button
                    type="button"
                    onClick={clearInput}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    title="Clear URL"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                ) : (
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !url.trim()}
              className={`px-8 py-4 text-lg sm:text-xl font-semibold text-white rounded-lg transition-all duration-200 min-w-[180px] sm:min-w-[200px] flex-shrink-0 flex items-center justify-center ${
                isLoading || !url.trim()
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'hover:shadow-lg transform hover:-translate-y-1'
              }`}
              style={{
                backgroundColor: !isLoading && url.trim() ? config.branding.colors.primary : undefined
              }}
            >
              {isLoading ? (
                <>
                  <div className="spinner mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  Download Now
                  <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                </>
              )}
            </button>
          </div>

          {/* Terms of Service Statement */}
          <div className="text-center">
            <div className="text-sm text-gray-600 leading-relaxed max-w-4xl mx-auto">
              By downloading, you agree to our{' '}
              <a
                href="/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline font-medium"
              >
                Terms of Service
              </a>
                              {' '}and confirm you'll comply with the third-party platform's terms and all applicable laws.
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-red-600 text-sm font-medium text-center bg-red-50 border border-red-200 rounded-lg p-3">
              {error}
            </div>
          )}

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center items-center gap-y-2 gap-x-4 sm:gap-x-6 text-sm text-gray-500">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              100% Free
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              No Registration
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              HD Quality
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              No Watermark
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DownloadForm;
